import { HoldingAllocationMode } from '@/enums/Portfolio';
import { DEFAULT_PORTFOLIO_SIZE } from '@/utils/portfolios';
import ComparisonItem from '@/types/comparison-item';
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import PortfolioHoldingType from '@/types/portfolio-holding-type';

/**
 * Converts a ComparisonItem back to a Portfolio structure.
 *
 * This is the reverse of `portfolioToComparisonItem`. It reconstructs a minimal Portfolio
 * from the data stored on a ComparisonItem, which is sufficient for use in group calculations
 * where only holdings weights and contact information are needed.
 *
 * @param compItem - The comparison item to convert.
 * @returns A Portfolio with holdings, contact, and size derived from the comparison item.
 */
const comparisonItemToPortfolio = (compItem: ComparisonItem): Portfolio => {
  const portfolioSize = compItem.size || DEFAULT_PORTFOLIO_SIZE;
  const portfolioHoldings: PortfolioHolding[] = compItem.holdings.map(h => ({
    _id: h.id,
    weight: h.weight,
    units: h.units,
    dollars: (h.weight / 100) * portfolioSize,
    currency: h.currency,
    symbol: h.symbol || null,
    name: h.name || { 'en-CA': '', 'fr-CA': '' },
    universe: h.universe,
    nav: h.nav,
    original: {
      id: h.id,
      type: h.type,
      yieldParams: h.yieldParams,
    },
  }));

  return {
    _id: compItem._id,
    correlationId: compItem._id,
    name: compItem.name,
    contact: compItem.contactsInfo?.[0],
    holdings: portfolioHoldings,
    size: portfolioSize,
    discretionaryMer: compItem.discretionaryMer,
    dates: {},
    allocationMode: HoldingAllocationMode.Weight,
  };
};

export default comparisonItemToPortfolio;
